import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCapIcon, MapPinIcon, PhoneIcon, MailIcon, CheckCircleIcon, BookOpenIcon, BriefcaseIcon, ChevronDownIcon, ChevronLeftIcon, AwardIcon, BuildingIcon, LoaderIcon } from 'lucide-react';
import { getUniversityBySlug, getProgramBySlug, University, UniversityProgram } from '../data/universities';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { TCPAConsent } from '../components/TCPAConsent';
import { TrustSignals } from '../components/TrustSignals';
import { usePageMeta } from '../hooks/usePageMeta';
const startDateOptions = ['Immediately', 'Within 1 month', 'Within 3 months', 'Within 6 months', 'Within 1 year', 'Not sure yet'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 30; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
export function UniversityPage() {
  const {
    universitySlug,
    programSlug
  } = useParams<{
    universitySlug: string;
    programSlug?: string;
  }>();
  const navigate = useNavigate();
  const [university, setUniversity] = useState<University | null>(null);
  usePageMeta(university ? `${university.name} Online Programs | OnlineCollegeGuide` : 'College Programs | OnlineCollegeGuide', university ? `Explore online degree programs at ${university.name}. ${university.description?.slice(0, 120) || 'Compare programs and request free information.'}` : 'Explore online degree programs at accredited colleges and universities.', {
    ogImage: university?.logoUrl || 'https://images.unsplash.com/photo-1562774053-701939374585?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const [selectedProgram, setSelectedProgram] = useState<UniversityProgram | null>(null);
  const [formStep, setFormStep] = useState(0); // 0=program select, 1=education, 2=zip/address, 3=contact
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  useEffect(() => {
    if (universitySlug) {
      const foundUni = getUniversityBySlug(universitySlug);
      if (foundUni) {
        setUniversity(foundUni);
        if (programSlug) {
          const foundProg = getProgramBySlug(foundUni, programSlug);
          if (foundProg) {
            setSelectedProgram(foundProg);
            if (formStep === 0) setFormStep(1);
          } else {
            setSelectedProgram(null);
          }
        } else {
          setSelectedProgram(null);
          setFormStep(0);
        }
      } else {
        setUniversity(null);
      }
    }
    window.scrollTo(0, 0);
  }, [universitySlug, programSlug]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    if (newSlug) {
      navigate(`/colleges/${universitySlug}/${newSlug}`, {
        replace: true
      });
    } else {
      navigate(`/colleges/${universitySlug}`, {
        replace: true
      });
    }
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        // Fallback: let user enter manually
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    if (validateUniStep2()) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = () => {
    if (validateUniStep3()) {
      const cleanedFormData = {
        ...formData,
        phone: stripPhone(formData.phone)
      };
      console.log('Lead submitted:', cleanedFormData);
      navigate('/thank-you', {
        state: {
          formData: cleanedFormData,
          partner: university ? {
            name: university.name,
            logo: university.logoUrl || '',
            primaryColor: '#1B365D',
            accentColor: '#D4A943',
            backUrl: `/colleges/${university.slug}`,
            programTitle: selectedProgram?.title || ''
          } : undefined
        }
      });
    }
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  if (!university) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-2524" data-mp="39li5cudx0ctg">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-2525" data-mp="34nglw8ckubhg">
          University Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-2526" data-mp="30lv180tswswp">
          We couldn't find the university you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-2527" data-mp="2vnbg22n17l5e">
          Return Home
        </Link>
      </div>;
  }
  const renderFormContent = () => {
    // Step 0: Just program selector (shown when no program selected)
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-2528" data-mp="2c1i676u4d473">
          <div data-id="element-2529" data-mp="2c2l1dqyh5sst">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2530" data-mp="2gzjse0mudjms">
              Select a Program
            </label>
            <div className="relative" data-id="element-2531" data-mp="34r8nfor1sm7f">
              <select id="hero-program-select" value={programSlug || ''} onChange={handleProgramChange} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-navy focus:border-navy font-medium text-gray-800" data-id="element-2532" data-mp="39oqrp1u4ed93">
                <option value="" data-id="element-2533" data-mp="3no9urg82qyej">-- Choose a Program --</option>
                {(() => {
                const groups: Record<string, typeof university.programs> = {};
                university.programs.forEach(p => {
                  const area = p.focusArea === '4' ? 'Computer Technology' : p.focusArea === '1' ? 'Business' : 'Other';
                  if (!groups[area]) groups[area] = [];
                  groups[area].push(p);
                });
                return Object.entries(groups).map(([area, progs]) => <optgroup key={area} label={area} data-id="element-2534" data-mp="1lpy2i850yfvj">
                      {progs.map(p => <option key={p.slug} value={p.slug} data-id="element-2535" data-mp="1lsu200witcm3">
                          {p.title}
                        </option>)}
                    </optgroup>);
              })()}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500" data-id="element-2536" data-mp="t45388ixtjdi">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2537" data-mp="122qk7z8rgqc6" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-2538" data-mp="2ig7aegs40xwh">
            Select a program above to get started
          </p>
        </div>;
    }
    // Step 1: Education level, start date, grad year
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-2539" data-mp="2xl1mluxtw0ih">
          {/* Program selector */}
          <div data-id="element-2540" data-mp="32ibmk4iufukk">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2541" data-mp="32jwf5olll3fs">
              Program
            </label>
            <div className="relative" data-id="element-2542" data-mp="3bawragpn40qe">
              <select id="step1-program" value={programSlug || ''} onChange={handleProgramChange} className="block w-full px-3 py-2.5 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-2543" data-mp="3bbyfznzkwlql">
                {(() => {
                const groups: Record<string, typeof university.programs> = {};
                university.programs.forEach(p => {
                  const area = p.focusArea === '4' ? 'Computer Technology' : p.focusArea === '1' ? 'Business' : 'Other';
                  if (!groups[area]) groups[area] = [];
                  groups[area].push(p);
                });
                return Object.entries(groups).map(([area, progs]) => <optgroup key={area} label={area} data-id="element-2544" data-mp="1sdik6c7ikoeg">
                      {progs.map(p => <option key={p.slug} value={p.slug} data-id="element-2545" data-mp="1nl8osgnmqw4s">
                          {p.title}
                        </option>)}
                    </optgroup>);
              })()}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400" data-id="element-2546" data-mp="1ellyor0cpcr1">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2547" data-mp="2bbl2y99gxp3t" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-2548" data-mp="26dbaw7j1j4mf" />

          {/* Education Level */}
          <div data-id="element-2549" data-mp="229lo9nlxex41">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2550" data-mp="2293i9z9gp4ix">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-2551" data-mp="qwj9my3e0hmi">
              <option value="" data-id="element-2552" data-mp="3e69j9yl20pvz">-- Please Select --</option>
              {educationLevels.map(level => <option key={level} value={level} data-id="element-2553" data-mp="396wrczfbssmj">
                  {level}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-2554" data-mp="3mzmjzmpd0vsm">
                {errors.currentEducation}
              </p>}
          </div>

          {/* Start Date */}
          <div data-id="element-2555" data-mp="p0jnx83vhz6">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2556" data-mp="oqv8e2plivl">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-2557" data-mp="9pozmxh27sep">
              <option value="" data-id="element-2558" data-mp="2bhtnnxvfu5k5">-- Please Select --</option>
              {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-2559" data-mp="27h1awuncxij4">
                  {opt}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-2560" data-mp="1xe4e1zmx4qks">{errors.startDate}</p>}
          </div>

          {/* Grad Year */}
          <div data-id="element-2561" data-mp="21dmy6t6x0xmk">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2562" data-mp="21g95qstt3d4s">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-2563" data-mp="1sk9yn8ecixri">
              <option value="" data-id="element-2564" data-mp="1io6ujwzxop1x">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-2565" data-mp="14op134bp0nm8">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-2566" data-mp="19nsa0zusndzh">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3" data-id="element-2567" data-mp="2z3hyn8w4zogt">
            <button type="button" onClick={() => navigate(`/colleges/${universitySlug}`, {
            replace: true
          })} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2568" data-mp="2z0c8dqbrugji">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2569" data-mp="2xndunydeeefv" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-2570" data-mp="2snri7r5e9284">
              Next
            </button>
          </div>
        </div>;
    }
    // Step 2: Zip code → city/state/address
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-2571" data-mp="3biy3g0zexg9k">
          <div data-id="element-2572" data-mp="36lpmg3q03i9w">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2573" data-mp="36m7pbj764uqs">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) {
              lookupZip(val);
            }
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-2574" data-mp="7xsanis9gv61" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-2575" data-mp="pd4qgu2njenu">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-2576" data-mp="tezn3l9n13va">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-2577" data-mp="tcwz16s1pkt7" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-2578" data-mp="135b3466lj3ij">
                <div data-id="element-2579" data-mp="184fd2z1ch59x">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2580" data-mp="182ui58b4bkbj">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="City" data-id="element-2581" data-mp="1h4aq79egnacl" />
                </div>
                <div data-id="element-2582" data-mp="2nlim7e0ig3vg">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2583" data-mp="2nkpdw8lwl6b7">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="State" data-id="element-2584" data-mp="1vub4opou4i5v" />
                </div>
              </div>

              <div data-id="element-2585" data-mp="1zv4ujolu871e">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2586" data-mp="1zumq44p0umie">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal normal-case" data-id="element-2587" data-mp="3bbuvas7hs6nm">
                    (optional)
                  </span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-2588" data-mp="3jr2b4ef0qu1a" />
              </div>
            </>}

          <div className="flex gap-3" data-id="element-2589" data-mp="3ev3puzozls53">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2590" data-mp="6d7arcfftf7p">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2591" data-mp="3ssih89ysw9zp" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-2592" data-mp="1l61lzdkg4lw">
              Next
            </button>
          </div>
        </div>;
    }
    // Step 3: Contact info + TCPA
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-2593" data-mp="yzntbtf2byyy">
          <div className="grid grid-cols-2 gap-3" data-id="element-2594" data-mp="z0qgprd6fja4">
            <div data-id="element-2595" data-mp="u0b4520pyft7">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2596" data-mp="tzt3lt70xffh">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-2597" data-mp="kzw9vd5s1qdk" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2598" data-mp="bbeb11h25tck">{errors.firstName}</p>}
            </div>

            <div data-id="element-2599" data-mp="6c1gdujy3hzf">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2600" data-mp="6ckzhelc0cf1">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-2601" data-mp="3tffz813buonc" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2602" data-mp="2dvyty2116rmc">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2603" data-mp="2hzwrw9emdwdt">
            <div data-id="element-2604" data-mp="2mxq017zhh0gq">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2605" data-mp="2my7z0cdj5ssc">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-2606" data-mp="1u90sor7u3ywj" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2607" data-mp="23ykeo5qug8vm">{errors.email}</p>}
            </div>

            <div data-id="element-2608" data-mp="16jgc2ez5ldqf">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2609" data-mp="16jzoxh9y5bxl">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-2610" data-mp="1bg6qyyz823a9" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2611" data-mp="dw5d83e564ky">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2" data-id="element-2612" data-mp="9t8pxzdnpkao">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-2613" data-mp="9weixppep6bo">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-navy focus:ring-navy cursor-pointer flex-shrink-0" data-id="element-2614" data-mp="nsi8h5pc4qht" />
              <span className="text-xs text-gray-600 leading-relaxed" data-id="element-2615" data-mp="skt8e8k2scad">
                By submitting this form, you are giving your express written
                consent to be contacted regarding our services using email,
                telephone or text, including our use of automated technology for
                calls or texts to the number provided above. This consent is not
                required to purchase goods or services. and you may always call
                us directly.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-2616" data-mp="1c61t28l3jx82">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3" data-id="element-2617" data-mp="1g6vgz1yjhc0y">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2618" data-mp="1ga178kiwmjy9">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2619" data-mp="sj433txcoqxk" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} className="flex-1 bg-crimson hover:bg-crimson-dark text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-2620" data-mp="xh5panq2p6d9">
              Submit
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <main className="flex-grow bg-light pb-16" data-id="element-2621" data-mp="mnc6sm6411hv">
      {/* College Header */}
      <header className="bg-white border-b border-gray-200" data-id="element-2622" data-mp="rngqel2beccb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between" data-id="element-2623" data-mp="dnffecsc5oj4">
          <Link to={`/colleges/${universitySlug}`} className="flex items-center" data-id="element-2624" data-mp="imioc8bfsewd">
            <img src="https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp" alt={`${university.name} logo`} className="h-14 md:h-16 w-auto object-contain" data-id="element-2625" data-mp="iod8hkevw2ze" />
          </Link>
          <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-2626" data-mp="1afj0teod4we4">
            <span className="hidden sm:inline-flex items-center" data-id="element-2627" data-mp="wlrzuuop6ku0">
              <PhoneIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-2628" data-mp="11kl37ua9svgg" />
              {university.phone}
            </span>
            <a href={`mailto:${university.email}`} className="hidden md:inline-flex items-center hover:text-navy transition-colors" data-id="element-2629" data-mp="1pa74cna7dkr3">
              <MailIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-2630" data-mp="1u3s0saw5e6o6" />
              {university.email}
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#1B365D] via-[#102442] to-[#8B1A1A]" data-id="element-2631" data-mp="1zeninc81atss">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-2632" data-mp="2d8eqmg64a5me" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10 md:py-16" data-id="element-2633" data-mp="1gi4d0wwfvo0z">
          <div className="inline-flex items-center text-gold font-semibold tracking-wider text-sm uppercase mb-6" data-id="element-2634" data-mp="1udq2142oa7te">
            <BuildingIcon className="w-4 h-4 mr-2" data-id="element-2635" data-mp="35uzjxm65krsl" />
            {university.name} &middot; {university.location}
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start" data-id="element-2636" data-mp="30wo9paby4f9u">
            {/* Left: Program Info */}
            <div className="lg:w-1/2 flex flex-col justify-center" data-id="element-2637" data-mp="38vsxrpuaxg5r">
              <AnimatePresence mode="wait" data-id="element-2638" data-mp="3cxmszilq9ryj">
                <motion.div key={selectedProgram?.slug || 'overview'} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.25
              }} data-id="element-2639" data-mp="3cwcu4r71wfqu">
                  <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-extrabold text-white mb-5 leading-tight" data-id="element-2640" data-mp="2u6wojpgidx3k">
                    {selectedProgram ? selectedProgram.title : `Explore Programs at ${university.name}`}
                  </h1>
                  <p className="text-base md:text-lg text-gray-200 leading-relaxed mb-6" data-id="element-2641" data-mp="33zko2ryvif5z">
                    {selectedProgram ? selectedProgram.description : university.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {!selectedProgram && <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium self-start" data-id="element-2642" data-mp="23wwcfdiit693">
                  <ChevronDownIcon className="w-4 h-4 mr-2 animate-bounce" data-id="element-2643" data-mp="1tyowmxzywu8o" />
                  Select a program to get started
                </div>}

              {selectedProgram && <div className="flex flex-wrap gap-3 text-sm text-gray-300" data-id="element-2644" data-mp="1kytgeerk291s">
                  {selectedProgram.keyFacts.slice(0, 2).map((fact, idx) => <span key={idx} className="inline-flex items-center bg-white/10 border border-white/15 rounded-full px-3 py-1" data-id="element-2645" data-mp="1g7to5mtdsf5m" data-mpi={idx}>
                      <CheckCircleIcon className="w-3.5 h-3.5 text-green-400 mr-1.5" data-id="element-2646" data-mp="1b99z3djhqn91" />
                      {fact}
                    </span>)}
                </div>}
            </div>

            {/* Right: Form Card */}
            <div className="lg:w-1/2 w-full flex justify-center lg:justify-end" data-id="element-2647" data-mp="11cnempv2ty7j">
              <div className="w-full max-w-md" data-id="element-2648" data-mp="xa0sn5l377i9">
                <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-2649" data-mp="1lzd9hwey9a3g">
                  <div className="bg-crimson px-6 py-3.5" data-id="element-2650" data-mp="1qz7ytvj2trme">
                    <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-2651" data-mp="1r0su5nd7telb">
                      Request Information
                    </h2>
                  </div>

                  {/* Progress Bar */}
                  {formStep > 0 && <div className="px-6 pt-4" data-id="element-2652" data-mp="138cax9hqrgkf">
                      <div className="flex justify-between items-center mb-1.5" data-id="element-2653" data-mp="13bqf8n9ytzui">
                        <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-2654" data-mp="189rxx73hbx5g">
                          Step {formStep} of {totalSteps}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-2655" data-mp="yasvw7d7yzrf">
                          {progressPercent}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-2656" data-mp="2solk7704ctjh">
                        <motion.div className="h-full bg-gold rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${progressPercent}%`
                    }} transition={{
                      duration: 0.4,
                      ease: 'easeOut'
                    }} data-id="element-2657" data-mp="h06wls2h6kyz" />
                      </div>
                    </div>}

                  <div className="p-6" data-id="element-2658" data-mp="10ofy4spws3b5">
                    <AnimatePresence mode="wait" data-id="element-2659" data-mp="10nec5t7ctwen">
                      <motion.div key={formStep} initial={{
                      opacity: 0,
                      x: 30
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} exit={{
                      opacity: 0,
                      x: -30
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-2660" data-mp="vq4mqdjytkqt">
                        {renderFormContent()}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                <p className="text-center text-gray-300 text-sm mt-3 italic" data-id="element-2661" data-mp="1ah45f513vbw1">
                  Used by{' '}
                  <strong className="text-white font-semibold" data-id="element-2662" data-mp="1kcysqdg9xv55">
                    2M+ students
                  </strong>{' '}
                  to choose the right school.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TrustSignals data-id="element-2663" data-mp="1tddgdjgpnmbt" />

      {/* Program Content — Full Width */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-2664" data-mp="1ybykhtz32q5j">
        <AnimatePresence mode="wait" data-id="element-2665" data-mp="233ow039rbjyj">
          {selectedProgram ? <motion.div key={selectedProgram.slug} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="space-y-8" data-id="element-2666" data-mp="233yktnj2fl2n">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2667" data-mp="2h3hj7om2ri1r">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-2668" data-mp="5gxi3sfcp9ng">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-2669" data-mp="g02nnlq6tpn">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-2670" data-mp="3sllr81zcgk7g">
                      <BriefcaseIcon className="w-6 h-6 text-navy" data-id="element-2671" data-mp="3nlr0byfkjume" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-2672" data-mp="o6ch7hbh7op7">
                      Potential Job Opportunities
                    </h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 font-medium" data-id="element-2673" data-mp="k5k9isi95rv1">
                    {selectedProgram.jobOpportunitiesIntro}:
                  </p>
                  <ul className="space-y-3" data-id="element-2674" data-mp="afh7y3x2f5l3">
                    {selectedProgram.jobOpportunities.map((job, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-2675" data-mp="17uv4ftcx8s19" data-mpi={idx}>
                        <span className="w-1.5 h-1.5 rounded-full bg-navy mt-1.5 mr-3 flex-shrink-0" data-id="element-2676" data-mp="133wj2fd2t3r5" />
                        <span data-id="element-2677" data-mp="bfjqtsg66998">{job}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-2678" data-mp="g71rsgi709u7">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-2679" data-mp="1ix756zkxo8s">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-2680" data-mp="6g75jc4y5a8r">
                      <AwardIcon className="w-6 h-6 text-navy" data-id="element-2681" data-mp="u6jmfci66hbh" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-2682" data-mp="u4qe7srwrp1d">Key Facts</h2>
                  </div>
                  <ul className="space-y-4" data-id="element-2683" data-mp="z39zdqyogwso">
                    {selectedProgram.keyFacts.map((fact, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-2684" data-mp="l7xu4pmwbnxi" data-mpi={idx}>
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" data-id="element-2685" data-mp="p5n968bxhite" />
                        <span className="leading-tight pt-0.5" data-id="element-2686" data-mp="35usppegbnuyd">{fact}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2687" data-mp="32d4z1n7174rf">
                <div className="bg-navy px-6 py-4 flex items-center" data-id="element-2688" data-mp="2xe1tm1n52wix">
                  <BookOpenIcon className="w-5 h-5 text-gold mr-3" data-id="element-2689" data-mp="3cc2iwctiia43" />
                  <h2 className="text-lg font-bold text-white" data-id="element-2690" data-mp="3ca9lzp8pb49a">
                    Program Curriculum
                  </h2>
                </div>
                <table className="min-w-full divide-y divide-gray-200" data-id="element-2691" data-mp="2jmvnvnt9o2nl">
                  <thead className="bg-gray-50" data-id="element-2692" data-mp="2eolthffd9631">
                    <tr data-id="element-2693" data-mp="2epwx9f8tadu5">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-2694" data-mp="2emheo2fximnz">
                        Course Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-2695" data-mp="2olgohocfkock">
                        Credits
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100" data-id="element-2696" data-mp="vebrnsnxsfbz">
                    {selectedProgram.curriculum.map((item, idx) => <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-id="element-2697" data-mp="qnlis5gaxucj" data-mpi={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2698" data-mp="lnz8o4vrsu7y">
                          {item.course}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-2699" data-mp="1edfuukssrevq">
                          {item.credits}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </motion.div> : <motion.div key="default-overview" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="space-y-8" data-id="element-2700" data-mp="14hcu9jdlfoaw">
              {/* Benefits of Online Learning */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-2701" data-mp="3p34of6c2mahl">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-2702" data-mp="3k8qznmrwz1lw">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-2703" data-mp="1zmahd7sf8ykw">
                    <CheckCircleIcon className="w-6 h-6 text-navy" data-id="element-2704" data-mp="24ohwu7cgmwrz" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-2705" data-mp="29li6stcfkjod">
                    Benefits of Online Learning with Global Tech College
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2706" data-mp="2jcvzah5ysj80">
                  {[{
                icon: <AwardIcon className="w-6 h-6 text-crimson" data-id="element-2707" data-mp="2nd6gyr34cy2p" />,
                title: 'High-Quality Education Experience'
              }, {
                icon: <BookOpenIcon className="w-6 h-6 text-crimson" data-id="element-2708" data-mp="2say6xfk7ea49" />,
                title: 'Flexible Online Schedules'
              }, {
                icon: <CheckCircleIcon className="w-6 h-6 text-crimson" data-id="element-2709" data-mp="xzjkb7mhoqsq" />,
                title: 'Accredited by ACCET'
              }, {
                icon: <BriefcaseIcon className="w-6 h-6 text-crimson" data-id="element-2710" data-mp="12ymkoeriy8ud" />,
                title: 'Career Services Assistance'
              }, {
                icon: <GraduationCapIcon className="w-6 h-6 text-crimson" data-id="element-2711" data-mp="qw35odjoz768" />,
                title: '1-1 Student Consulting'
              }, {
                icon: <BuildingIcon className="w-6 h-6 text-crimson" data-id="element-2712" data-mp="14we1zw3rtdys" />,
                title: 'Very Low Tuition Fees'
              }].map((item, idx) => <div key={idx} className="bg-gray-50 rounded-xl border border-gray-100 p-5 flex items-center gap-4" data-id="element-2713" data-mp="zyumksvc6v59" data-mpi={idx}>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-sm flex-shrink-0" data-id="element-2714" data-mp="19uxkfmj5o7me">
                        {item.icon}
                      </div>
                      <h3 className="font-bold text-navy text-sm leading-tight" data-id="element-2715" data-mp="1in9dzh9kgb6l">
                        {item.title}
                      </h3>
                    </div>)}
                </div>
              </div>

              {/* Highlights Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2716" data-mp="1smqgjptiu8yc">
                {[{
              icon: <AwardIcon className="w-6 h-6 text-crimson" data-id="element-2717" data-mp="2c7qkfmvau4fk" />,
              title: 'Title IV Approved',
              desc: 'Federal financial aid available for eligible students'
            }, {
              icon: <GraduationCapIcon className="w-6 h-6 text-crimson" data-id="element-2718" data-mp="22avptf25co6k" />,
              title: 'VA Benefits',
              desc: 'Approved for veterans and dependents education benefits'
            }, {
              icon: <CheckCircleIcon className="w-6 h-6 text-crimson" data-id="element-2719" data-mp="1sgve2ucd0kov" />,
              title: 'ACCET Accredited',
              desc: 'Accrediting Council for Continuing Education & Training'
            }, {
              icon: <BriefcaseIcon className="w-6 h-6 text-crimson" data-id="element-2720" data-mp="1jggq1n80grho" />,
              title: 'Financial Aid',
              desc: 'Available for students who qualify'
            }].map((item, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 text-center" data-id="element-2721" data-mp="19kvr5xy8o7c5" data-mpi={idx}>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-crimson/5 mb-3" data-id="element-2722" data-mp="1el1cyxhtuzmb">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-navy text-sm mb-1" data-id="element-2723" data-mp="2cvt4s3riylb2">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-2724" data-mp="2357xzt1p6ah2">{item.desc}</p>
                  </div>)}
              </div>

              {/* Available Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-2725" data-mp="1y7om2zsh29sa">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-2726" data-mp="1u2ypf8pzor6b">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-2727" data-mp="1p68a4nm0dzly">
                    <BookOpenIcon className="w-6 h-6 text-navy" data-id="element-2728" data-mp="1k6m00n1h8zhd" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-2729" data-mp="3jjgcr1g87fi5">
                    Available Programs
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2730" data-mp="3elogxwcgn98o">
                  {university.programs.map(prog => <button key={prog.slug} onClick={() => navigate(`/colleges/${universitySlug}/${prog.slug}`, {
                replace: true
              })} className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-navy/30 hover:bg-navy/[0.02] transition-all text-left group" data-id="element-2731" data-mp="3m3cwy3ae1v1g">
                      <div className="p-1.5 bg-navy/5 rounded-md group-hover:bg-navy/10 transition-colors mt-0.5" data-id="element-2732" data-mp="4mmnyxew75u7">
                        <GraduationCapIcon className="w-4 h-4 text-navy" data-id="element-2733" data-mp="337egijqopla9" />
                      </div>
                      <div data-id="element-2734" data-mp="385y3mnh0l347">
                        <h3 className="font-semibold text-navy text-sm group-hover:text-navy-light transition-colors" data-id="element-2735" data-mp="3d2ok3bwqe0q5">
                          {prog.title}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-2736" data-mp="sd8wepst6s4q">
                          {prog.degreeLevel === '1' ? 'Certificate' : 'Associate Degree'}{' '}
                          · {prog.keyFacts[0]}
                        </p>
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Accreditation */}
              <div className="bg-navy/5 rounded-xl border border-navy/10 p-6 flex items-start gap-4" data-id="element-2737" data-mp="2oi7xgnlqpe3j">
                <div className="p-2 bg-white rounded-lg shadow-sm" data-id="element-2738" data-mp="32dseteltlo8u">
                  <AwardIcon className="w-6 h-6 text-navy" data-id="element-2739" data-mp="2yfmbkgk6xqso" />
                </div>
                <div data-id="element-2740" data-mp="2anxc8g8yc228">
                  <h3 className="font-bold text-navy mb-1" data-id="element-2741" data-mp="2ap8hyllyjjvz">Accreditation</h3>
                  <p className="text-sm text-gray-600" data-id="element-2742" data-mp="25qov8izji42k">
                    The Accrediting Council for Continuing Education & Training
                    (ACCET)
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-2743" data-mp="2epsepo2mclhl">
                    {university.location}
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </main>;
}